(() => {
  'use strict';

  const VERSION = '2026-08-15-v8-p1-surgical';
  const DESKTOP = window.matchMedia('(min-width:990px) and (hover:hover) and (pointer:fine)');
  const PRODUCT_LINK = 'a[href*="/products/"]';
  const TILE_SELECTOR = '[data-serrano-variant-tile]';
  const EXCLUDE_SELECTOR = '[data-cart-item],.cart-item,[data-cart-drawer],[id*="CartDrawer"]';
  const CARD_SELECTOR = [
    '.product-tile',
    '.showcase-tile',
    '#results-grid .grid__item',
    '.sms-card',
    '.dn-ready-card',
    '.serrano-pdp-ready-products__item',
    '[data-product-card]'
  ].join(',');

  const cache = new Map();
  const builtStages = new WeakSet();
  const stateByTile = new WeakMap();
  const pendingBuilds = new WeakMap();

  function installStyles() {
    [
      'serrano-universal-card-adapter-css',
      'serrano-global-gallery-css',
      'serrano-multipack-hover-lock-css',
      'serrano-product-gallery-v7-css'
    ].forEach((id) => document.getElementById(id)?.remove());

    const style = document.createElement('style');
    style.id = 'serrano-product-gallery-v7-css';
    style.textContent = `
      [data-serrano-gallery-managed]{position:relative!important}
      [data-serrano-gallery-managed] .product-media-object,
      [data-serrano-gallery-managed] .product-media-object img,
      [data-serrano-gallery-managed] .group:hover .product-media-object,
      [data-serrano-gallery-managed] .group:hover .product-media-object img,
      [data-serrano-gallery-managed] .product-tile__featured-media:hover .product-media-object,
      [data-serrano-gallery-managed] .product-tile__featured-media:hover .product-media-object img{
        transform:none!important;scale:1!important;
        --tw-translate-x:0!important;--tw-translate-y:0!important;--tw-rotate:0!important;
        --tw-scale-x:1!important;--tw-scale-y:1!important;--hover-effect-zoom-media:1!important
      }
      .serrano-v7-stage{position:relative!important;z-index:20!important;overflow:hidden!important}
      .serrano-v7-layer{position:absolute;inset:0;z-index:30;background:inherit;overflow:hidden}
      .serrano-v7-layer .serrano-variant-tile__track{position:absolute;inset:0;width:100%;height:100%;z-index:31;background:inherit}
      .serrano-v7-layer .serrano-variant-tile__slide{height:100%;background:inherit}
      .serrano-v7-layer .serrano-variant-tile__image{width:100%!important;height:100%!important;max-width:none!important;background:inherit;transform:none!important;scale:1!important}
      [data-serrano-variant-prev],[data-serrano-variant-next]{z-index:999!important;pointer-events:auto!important;touch-action:manipulation!important}
      [data-serrano-variant-prev][aria-disabled="true"],[data-serrano-variant-next][aria-disabled="true"]{pointer-events:none!important}
      .serrano-v7-layer [data-serrano-variant-counter]{z-index:998!important}
      [data-serrano-gallery-managed] a.absolute.left-0.top-0.z-10{z-index:5!important}
    `;
    document.head.appendChild(style);
    document.documentElement.dataset.serranoGalleryVersion = VERSION;
  }

  function handleFromUrl(href) {
    try {
      const match = new URL(href, document.baseURI || location.href).pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  }

  function imageKey(src) {
    try {
      return decodeURIComponent(new URL(src, document.baseURI || location.href).pathname);
    } catch (_) {
      return String(src || '').split('?')[0];
    }
  }

  function resized(src) {
    try {
      const url = new URL(src, document.baseURI || location.href);
      url.searchParams.set('width', '800');
      return url.href;
    } catch (_) {
      return src;
    }
  }

  function uniqueImages(items, baseSource = '') {
    const out = [];
    const seen = new Set();
    for (const item of items || []) {
      const src = typeof item === 'string' ? item : (item?.src || item?.url || '');
      const key = imageKey(src);
      if (!src || !key || seen.has(key)) continue;
      seen.add(key);
      out.push(src);
    }

    const baseKey = imageKey(baseSource);
    const index = out.findIndex((src) => imageKey(src) === baseKey);
    if (index > 0) out.unshift(out.splice(index, 1)[0]);
    else if (index < 0 && baseSource) out.unshift(baseSource);
    return out;
  }

  function fetchProduct(productHandle) {
    if (cache.has(productHandle)) return cache.get(productHandle);
    const root = window.Shopify?.routes?.root || '/';
    const request = fetch(`${root}products/${encodeURIComponent(productHandle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then((response) => {
      if (!response.ok) throw new Error(`Product ${productHandle} could not be loaded`);
      return response.json();
    });
    cache.set(productHandle, request);
    return request;
  }

  function inlineGalleryData(card) {
    const node = card?.querySelector?.('script[data-serrano-gallery-data]');
    if (!node) return null;
    try {
      const data = JSON.parse(node.textContent || '{}');
      return Array.isArray(data.images) && data.images.length ? data : null;
    } catch (_) {
      return null;
    }
  }

  function productLink(card) {
    const links = Array.from(card.querySelectorAll(PRODUCT_LINK));
    return links.find((link) => link.querySelector('img')) || links[0] || null;
  }

  function baseImage(card, link) {
    return link?.querySelector('img:not(.serrano-variant-tile__image)') || card.querySelector(
      '.product-media-object img:not(.serrano-variant-tile__image),' +
      '.media__styled-element img:not(.serrano-variant-tile__image),' +
      '.media__shape-wrapper img:not(.serrano-variant-tile__image),' +
      '.object-wrapper img:not(.serrano-variant-tile__image),' +
      '.sms-media img:not(.serrano-variant-tile__image),' +
      '.dn-ready-card__media img:not(.serrano-variant-tile__image),' +
      '.serrano-pdp-ready-products__media img:not(.serrano-variant-tile__image),' +
      'img:not(.serrano-variant-tile__image)'
    );
  }

  function mediaStage(image) {
    return image.closest(
      '.media__styled-element,.media__shape-wrapper,.object-wrapper,.sms-media,' +
      '.dn-ready-card__media,.serrano-pdp-ready-products__media,.product-media-object,.media'
    ) || image.parentElement;
  }

  function makeArrow(direction, title) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `serrano-variant-tile__arrow serrano-variant-tile__arrow--${direction}`;
    button.setAttribute(direction === 'prev' ? 'data-serrano-variant-prev' : 'data-serrano-variant-next', '');
    button.setAttribute('aria-label', `${title} ${direction === 'prev' ? 'önceki' : 'sonraki'} görsel`);
    button.innerHTML = direction === 'prev'
      ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 5 7.5 12l7 7" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9.5 5 7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return button;
  }

  function buildGallery(card, stage, base, link, title, images) {
    if (!card || !stage || builtStages.has(stage)) return;
    const list = uniqueImages(images, base.currentSrc || base.src || '');
    if (list.length < 2) return;

    builtStages.add(stage);
    stage.querySelectorAll(':scope > [data-serrano-universal-gallery],:scope > .sg-v3-track,:scope > .sg-track').forEach((node) => node.remove());

    const layer = document.createElement('div');
    const track = document.createElement('div');
    const previous = makeArrow('prev', title);
    const next = makeArrow('next', title);
    const counter = document.createElement('span');
    const baseStyle = getComputedStyle(base);

    layer.className = 'serrano-v7-layer';
    layer.setAttribute('data-serrano-universal-gallery', VERSION);
    track.className = 'serrano-variant-tile__track';
    track.setAttribute('data-serrano-variant-track', '');
    track.tabIndex = 0;
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', `${title} görselleri`);
    counter.className = 'serrano-variant-tile__counter';
    counter.setAttribute('data-serrano-variant-counter', '');
    counter.textContent = `1 / ${list.length}`;

    list.forEach((source, index) => {
      const slide = document.createElement('a');
      const image = document.createElement('img');
      slide.className = 'serrano-variant-tile__slide';
      slide.setAttribute('data-serrano-variant-slide', '');
      slide.href = link.href;
      slide.setAttribute('aria-label', `${title} ürününü aç`);
      image.className = 'serrano-variant-tile__image';
      image.src = resized(source);
      image.alt = `${title} – Görsel ${index + 1}`;
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      image.style.objectFit = baseStyle.objectFit || 'cover';
      image.style.objectPosition = baseStyle.objectPosition || 'center';
      image.style.borderRadius = baseStyle.borderRadius;
      image.style.clipPath = baseStyle.clipPath;
      slide.appendChild(image);
      track.appendChild(slide);
    });

    layer.append(track, previous, next, counter);
    stage.classList.add('serrano-v7-stage');
    card.classList.add('serrano-universal-gallery-managed');
    card.setAttribute('data-serrano-gallery-managed', VERSION);
    card.setAttribute('data-serrano-variant-tile', '');
    stage.appendChild(layer);
    initialiseTile(card);
  }

  function hydrateCard(card) {
    if (!card || card.closest(EXCLUDE_SELECTOR)) return Promise.resolve(null);

    if (card.matches(TILE_SELECTOR) && card.querySelectorAll('[data-serrano-variant-slide]').length > 1) {
      card.setAttribute('data-serrano-gallery-managed', VERSION);
      initialiseTile(card);
      return Promise.resolve(card);
    }

    if (pendingBuilds.has(card)) return pendingBuilds.get(card);

    const link = productLink(card);
    if (!link) return Promise.resolve(null);
    const base = baseImage(card, link);
    const stage = base && mediaStage(base);
    if (!base || !stage) return Promise.resolve(null);
    if (stage.querySelector('[data-serrano-universal-gallery]')) {
      card.setAttribute('data-serrano-gallery-managed', VERSION);
      card.setAttribute('data-serrano-variant-tile', '');
      initialiseTile(card);
      return Promise.resolve(card);
    }

    const embedded = inlineGalleryData(card);
    if (embedded) {
      buildGallery(card, stage, base, link, embedded.title || 'Ürün', embedded.images || []);
      return Promise.resolve(card);
    }

    const productHandle = handleFromUrl(link.getAttribute('href') || link.href);
    if (!productHandle) return Promise.resolve(null);

    const request = fetchProduct(productHandle)
      .then((data) => {
        buildGallery(card, stage, base, link, data.title || 'Ürün', data.images || []);
        return card;
      })
      .catch(() => null);
    pendingBuilds.set(card, request);
    return request;
  }

  function registerCard(card) {
    if (!card || card.closest(EXCLUDE_SELECTOR)) return;
    if (card.matches(TILE_SELECTOR) && card.querySelectorAll('[data-serrano-variant-slide]').length > 1) {
      card.setAttribute('data-serrano-gallery-managed', VERSION);
      initialiseTile(card);
      return;
    }
    if (productLink(card)) card.setAttribute('data-serrano-gallery-candidate', VERSION);
  }

  function registerLooseLink(link) {
    if (!link || link.closest(EXCLUDE_SELECTOR)) return;
    if (!handleFromUrl(link.getAttribute('href') || link.href)) return;
    const card = link.closest(CARD_SELECTOR);
    if (card) registerCard(card);
    else if (link.querySelector('img')) registerCard(link);
  }

  function stateFor(tile) {
    if (!tile) return null;
    let state = stateByTile.get(tile);
    const track = tile.querySelector('[data-serrano-variant-track]');
    const slides = Array.from(tile.querySelectorAll('[data-serrano-variant-slide]'));
    if (!track || slides.length < 2) return null;

    if (!state || state.track !== track || state.slides.length !== slides.length) {
      state = { tile, track, slides, index: 0, manual: false, frame: 0 };
      stateByTile.set(tile, state);
    }
    return state;
  }

  function syncControls(state, explicitIndex = null) {
    if (!state) return;
    const { tile, track, slides } = state;
    const width = track.clientWidth || 1;
    const index = explicitIndex == null
      ? Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / width)))
      : Math.max(0, Math.min(slides.length - 1, explicitIndex));
    state.index = index;

    const previous = tile.querySelector('[data-serrano-variant-prev]');
    const next = tile.querySelector('[data-serrano-variant-next]');
    const counter = tile.querySelector('[data-serrano-variant-counter]');
    if (previous) {
      previous.disabled = index === 0;
      previous.setAttribute('aria-disabled', index === 0 ? 'true' : 'false');
    }
    if (next) {
      next.disabled = index === slides.length - 1;
      next.setAttribute('aria-disabled', index === slides.length - 1 ? 'true' : 'false');
    }
    if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
  }

  function goTo(tile, target, smooth = true) {
    const state = stateFor(tile);
    if (!state) return;
    const index = Math.max(0, Math.min(state.slides.length - 1, target));
    const width = state.track.clientWidth || 1;
    const left = index * width;
    state.index = index;
    state.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    syncControls(state, index);
  }

  function initialiseTile(tile) {
    const state = stateFor(tile);
    if (!state) return;
    tile.setAttribute('data-serrano-gallery-managed', VERSION);
    tile.dataset.serranoCarouselReady = VERSION;

    if (state.track.__serranoScrollVersion !== VERSION) {
      state.track.__serranoScrollVersion = VERSION;
      state.track.addEventListener('scroll', () => {
        if (state.frame) cancelAnimationFrame(state.frame);
        state.frame = requestAnimationFrame(() => syncControls(state));
      }, { passive: true });
    }
    syncControls(state);
  }

  function tileFromEventTarget(target) {
    return target?.closest?.(TILE_SELECTOR) || null;
  }

  document.addEventListener('pointerover', (event) => {
    if (!DESKTOP.matches) return;
    const existingTile = tileFromEventTarget(event.target);
    if (existingTile) {
      if (existingTile.contains(event.relatedTarget)) return;
      const state = stateFor(existingTile);
      if (!state) return;
      state.manual = false;
      goTo(existingTile, 1, false);
      return;
    }

    const card = event.target?.closest?.(CARD_SELECTOR);
    if (!card || card.contains(event.relatedTarget)) return;
    hydrateCard(card).then((tile) => {
      if (!tile || !tile.matches(':hover')) return;
      const state = stateFor(tile);
      if (!state) return;
      state.manual = false;
      goTo(tile, 1, false);
    });
  }, true);

  document.addEventListener('pointerout', (event) => {
    if (!DESKTOP.matches) return;
    const tile = tileFromEventTarget(event.target);
    if (!tile || tile.contains(event.relatedTarget)) return;
    const state = stateFor(tile);
    if (!state) return;
    state.manual = false;
    goTo(tile, 0, false);
  }, true);

  document.addEventListener('focusin', (event) => {
    const card = event.target?.closest?.(CARD_SELECTOR);
    if (card) hydrateCard(card);
  }, true);

  document.addEventListener('pointerdown', (event) => {
    const card = event.target?.closest?.(CARD_SELECTOR);
    if (card && !card.matches(TILE_SELECTOR)) hydrateCard(card);
  }, true);

  document.addEventListener('pointerdown', (event) => {
    if (!event.target.closest?.('[data-serrano-variant-prev],[data-serrano-variant-next]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('[data-serrano-variant-prev],[data-serrano-variant-next]');
    if (!control) return;
    const tile = control.closest(TILE_SELECTOR);
    const state = stateFor(tile);
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!state || control.getAttribute('aria-disabled') === 'true' || control.disabled) return;
    syncControls(state);
    state.manual = true;
    goTo(tile, state.index + (control.matches('[data-serrano-variant-next]') ? 1 : -1), true);
  }, true);

  document.addEventListener('keydown', (event) => {
    const control = event.target.closest?.('[data-serrano-variant-prev],[data-serrano-variant-next]');
    if (control && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      control.click();
      return;
    }
    const track = event.target.closest?.('[data-serrano-variant-track]');
    if (!track || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
    const tile = track.closest(TILE_SELECTOR);
    const state = stateFor(tile);
    if (!state) return;
    event.preventDefault();
    syncControls(state);
    state.manual = true;
    goTo(tile, state.index + (event.key === 'ArrowRight' ? 1 : -1), true);
  }, true);

  let pointer = null;
  document.addEventListener('pointerdown', (event) => {
    const track = event.target.closest?.('[data-serrano-variant-track]');
    if (!track) return;
    pointer = { track, x: event.clientX, y: event.clientY, moved: false };
  }, true);
  document.addEventListener('pointermove', (event) => {
    if (!pointer) return;
    if (Math.abs(event.clientX - pointer.x) > 8 && Math.abs(event.clientX - pointer.x) > Math.abs(event.clientY - pointer.y)) pointer.moved = true;
  }, { capture: true, passive: true });
  document.addEventListener('click', (event) => {
    const track = event.target.closest?.('[data-serrano-variant-track]');
    if (!track || !pointer || pointer.track !== track || !pointer.moved || (pointer.expires && Date.now() > pointer.expires)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    pointer = null;
  }, true);
  document.addEventListener('pointerup', () => {
    if (!pointer) return;
    if (!pointer.moved) pointer = null;
    else pointer.expires = Date.now() + 500;
  }, true);
  document.addEventListener('pointercancel', () => { pointer = null; }, true);

  function scan(root = document) {
    const scope = root?.querySelectorAll ? root : document;
    scope.querySelectorAll(TILE_SELECTOR).forEach(initialiseTile);
    scope.querySelectorAll(CARD_SELECTOR).forEach(registerCard);
    scope.querySelectorAll(PRODUCT_LINK).forEach(registerLooseLink);
    if (scope.matches?.(TILE_SELECTOR)) initialiseTile(scope);
    if (scope.matches?.(CARD_SELECTOR)) registerCard(scope);
    if (scope.matches?.(PRODUCT_LINK)) registerLooseLink(scope);
  }

  function init() {
    installStyles();
    scan(document);
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
    document.addEventListener('shapes:section:hasmutated', (event) => scan(event.target));
  }

  window.SerranoProductCardGallery = { version: VERSION, scan, goTo, hydrate: hydrateCard };
  window.__serranoVariantTileCarousel = { version: VERSION, scan, initialise: initialiseTile };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
